/**
 * Migration Banner Component
 *
 * Displays a dismissible warning banner for organizations without fiscal periods.
 * Stores dismissal state in localStorage per organization.
 */

import { useState, useEffect } from 'react'

import { Link } from 'react-router-dom'

import { Button } from './ui'
import { hasAnyFiscalPeriods } from '../services/fiscal-period-service'
import { useOrganization } from '../stores/organization-store'

const DISMISSAL_KEY_PREFIX = 'fiscal-period-migration-dismissed-'

async function checkOrgPeriods(organizationId: string, setHasPeriods: (v: boolean) => void, setIsChecking: (v: boolean) => void): Promise<void> {
  try {
    const hasPeriodsDefined = await hasAnyFiscalPeriods(organizationId)
    setHasPeriods(hasPeriodsDefined)
  } catch {
    setHasPeriods(true)
  } finally {
    setIsChecking(false)
  }
}

function BannerContent({ onDismiss }: { onDismiss: () => void }): JSX.Element {
  return (
    <div className="ml-3 flex-1">
      <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Action Required: Configure Fiscal Periods</h3>
      <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
        Your organization does not have fiscal periods configured yet. Fiscal periods are now required to post journal entries and organize your financial transactions.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Link to="/periods"><Button size="sm" variant="primary">Configure Now</Button></Link>
        <button className="text-sm font-medium text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200" onClick={onDismiss}>Dismiss</button>
      </div>
    </div>
  )
}

export function MigrationBanner(): JSX.Element | null {
  const { organizationId } = useOrganization()
  const [hasPeriods, setHasPeriods] = useState<boolean | null>(null)
  const [isDismissed, setIsDismissed] = useState(false)
  const [isChecking, setIsChecking] = useState(true)

  useEffect(() => {
    if (!organizationId) { setIsChecking(false); return }
    const dismissalKey = `${DISMISSAL_KEY_PREFIX}${organizationId}`
    const didDismiss = localStorage.getItem(dismissalKey) === 'true'
    setIsDismissed(didDismiss)
    if (didDismiss) { setIsChecking(false); return }
    void checkOrgPeriods(organizationId, setHasPeriods, setIsChecking)
  }, [organizationId])

  const handleDismiss = (): void => {
    if (!organizationId) { return }
    localStorage.setItem(`${DISMISSAL_KEY_PREFIX}${organizationId}`, 'true')
    setIsDismissed(true)
  }

  if (isChecking || isDismissed || hasPeriods !== false || !organizationId) { return null }

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 dark:border-blue-600 p-4 mb-6">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path clipRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" fillRule="evenodd" />
          </svg>
        </div>
        <BannerContent onDismiss={handleDismiss} />
        <div className="ml-3 flex-shrink-0">
          <button className="inline-flex rounded-md p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={handleDismiss}>
            <span className="sr-only">Dismiss</span>
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path clipRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" fillRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
